import { useCallback } from "react";
import { useStore } from "react-redux";

import { useAppDispatch } from "app/redux-store-interactions";
import { type RootState } from "app/store";
import { measurePickers } from "components/toolbars/implementations/pickerGroups";
import { bottomToolbarActions } from "components/toolbars/mobile/slice";
import {
    coordinatesActions,
    selectCoordinates,
    selectCoordinatesEditSessionStartSnapshot,
} from "features/coordinates/coordinatesSlice";
import {
    measureActions,
    selectObjectMeasure,
    selectObjectMeasureEditSessionStartSnapshot,
} from "features/measure/measureSlice";
import { turnOffObjectMeasureXray } from "features/measure/objectMeasureXray";
import { mobileMeasureActions, selectMobileMeasure } from "features/mobileMeasure/mobileMeasureSlice";
import {
    clippingOutlineLaserActions,
    selectLaserEditSessionStartSnapshot,
    selectOutlineLasers,
} from "features/outlineLaser/outlineLaserSlice";
import { pointLineActions, selectPointLineCurrentIdx, selectPointLines } from "features/pointLine";
import { selectPointLineEditSessionStartSnapshot } from "features/pointLine/pointLineSlice";
import {
    renderActions,
    selectClippingEditSessionStartSnapshot,
    selectClippingPlanes,
    selectPicker,
} from "features/render";
import { undoRedoActions } from "features/undoRedo";
import { ProcoreToolMode, procoreUxActions, selectActiveToolMode } from "slices/procoreUx";

/**
 * Returns a cancel callback that reverts all in-session changes by restoring each affected
 * slice's data from the snapshot taken at edit-session start, then exits the current mode.
 * Used by ToolModeConfirmationToolbar's Close button, the Escape shortcut, and BottomToolbar.
 */
export function useCancelCurrentModeChanges() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();

    return useCallback(() => {
        const state = store.getState();
        const picker = selectPicker(state);
        const activeToolMode = selectActiveToolMode(state);
        const isActive = activeToolMode !== ProcoreToolMode.Default;
        if (!isActive) {
            return false;
        }

        // Capture pre-cancel state so undo can fully restore it.
        const lasersBefore = selectOutlineLasers(state);
        const pointLinesBefore = selectPointLines(state);
        const pointLineCurrentIdxBefore = selectPointLineCurrentIdx(state);
        const coordinatesBefore = selectCoordinates(state);
        const laserSnapshotBefore = selectLaserEditSessionStartSnapshot(state);
        const pointLineSnapshotBefore = selectPointLineEditSessionStartSnapshot(state);
        const coordinatesSnapshotBefore = selectCoordinatesEditSessionStartSnapshot(state);
        const clippingPlanesBefore = selectClippingPlanes(state).planes;
        const clippingSnapshotBefore = selectClippingEditSessionStartSnapshot(state);
        const objectMeasureBefore = selectObjectMeasure(state);
        const objectMeasureSnapshotBefore = selectObjectMeasureEditSessionStartSnapshot(state);
        const isMeasurePicker = measurePickers.includes(picker);
        const mobileMeasureBefore = isMeasurePicker ? selectMobileMeasure(state) : null;

        const apply = () => {
            if (isMeasurePicker) {
                dispatch(mobileMeasureActions.reset());
            }

            // TODO: enable when Dimension tool is back
            // if (picker === Picker.Dimension) {
            //     const measure = selectMeasure(state);
            //     const dimensionSessionStart = selectDimensionSessionStart(state);
            //     const total = measure.selectedEntities.length;
            //     for (let i = total - 1; i >= dimensionSessionStart; i--) {
            //         dispatch(measureActions.deleteMeasureSet(i));
            //     }
            // }

            // Each slice owns its snapshot shape and restoration logic. These are no-ops
            // when no snapshot is active.
            dispatch(renderActions.restoreClippingEditSessionStartSnapshot());
            dispatch(clippingOutlineLaserActions.restoreEditSessionStartSnapshot());
            dispatch(pointLineActions.restoreEditSessionStartSnapshot());
            dispatch(coordinatesActions.restoreEditSessionStartSnapshot());
            dispatch(measureActions.restoreObjectMeasureEditSessionStartSnapshot());
            // The x-ray the object-measure tool switched on goes with it, whether or not the
            // measurement it was showing off survives the cancel.
            turnOffObjectMeasureXray(dispatch, store.getState());

            dispatch(procoreUxActions.setActiveToolMode(ProcoreToolMode.Default));
            dispatch(renderActions.stopPicker(picker));

            dispatch(bottomToolbarActions.setActiveTab("tools"));
        };

        apply();

        dispatch(
            undoRedoActions.push({
                description: "Cancel current mode changes",
                undoCallback: () => {
                    dispatch(procoreUxActions.setActiveToolMode(activeToolMode));
                    dispatch(renderActions.setPicker(picker));
                    dispatch(clippingOutlineLaserActions.setLasers(lasersBefore));
                    dispatch(pointLineActions.setPointLines(pointLinesBefore));
                    dispatch(pointLineActions.setCurrent(pointLineCurrentIdxBefore));
                    dispatch(coordinatesActions.setCoordinates(coordinatesBefore));
                    dispatch(clippingOutlineLaserActions.setEditSessionStartSnapshot(laserSnapshotBefore));
                    dispatch(pointLineActions.setEditSessionStartSnapshot(pointLineSnapshotBefore));
                    dispatch(coordinatesActions.setEditSessionStartSnapshot(coordinatesSnapshotBefore));
                    dispatch(renderActions.setClippingPlanes({ planes: clippingPlanesBefore }));
                    dispatch(renderActions.setClippingEditSessionStartSnapshot(clippingSnapshotBefore));
                    dispatch(measureActions.setObjectMeasure(objectMeasureBefore));
                    dispatch(measureActions.setObjectMeasureEditSessionStartSnapshot(objectMeasureSnapshotBefore));
                    if (mobileMeasureBefore) {
                        dispatch(mobileMeasureActions.setState(mobileMeasureBefore));
                    }
                },
                redoCallback: apply,
            }),
        );

        return true;
    }, [dispatch, store]);
}
